import {
  setLocalStorage,
  getFromLocalStorage,
  removeLocalStorageKey,
} from "~/utils/cookies/cookies-client";

/* userAttribution is stored in localStorage and NEVER expires */
/* value = { first: {date, utms}, last: {date, utms} } */
const USER_ATTRIBUTION = "userAttribution";

export const setUserAttribution = (queryString) => {
  if (!queryString || Object.keys(queryString).length === 0) return;

  const currentValue = getUserAttribution();
  const now = Date.now();
  const newValue = currentValue || { first: { date: now, utms: queryString } };

  newValue.last = { date: now, utms: queryString };

  setLocalStorage(USER_ATTRIBUTION, newValue);
};

export const getUserAttribution = () => {
  return getFromLocalStorage(USER_ATTRIBUTION);
};

/* introSettings is stored in localStorage and expires in 1 year */
/* value = { last_updated, answers, quizToken, fwcQuantity, pricingPlanSlug, isDoubleOrder, key } */
const INTRO_SETTINGS_LIFE = 1000 * 3600 * 24 * 365;
const INTRO_SETTINGS = "introSettings";
const settingsKeys = [
  "last_updated",
  "answers",
  "quizToken",
  // The Amplitude device id belongs here: it identifies a browser and does not
  // expire, so restoring it on a later visit is still true.
  //
  // The Amplitude session id deliberately does not. A session is a window - the
  // cookie reader drops one older than 15 minutes for exactly this reason - and
  // this store lives for a year, so a restored session would be stamped on the
  // cart as if it were current and join the order to a visit it was not part of.
  // Keeping it in memory is enough for the only place that reads it back, a box
  // change within the same visit.
  "deviceId",
  "promoCode",
  "pricingPlanSlug",
  "skipEmail",
  "fwcQuantity",
  "stateCode",
  "boxComposition",
  "clubFrequency",
];

export const setIntroSettings = (state) => {
  const currentValue = getIntroSettings();
  const newSettings = state.settings;

  const dataWithValue = Object.fromEntries(
    Object.entries(newSettings).filter(
      ([key, val]) =>
        settingsKeys.includes(key) && val != null && typeof val !== "undefined",
    ),
  );

  dataWithValue.last_updated = Date.now();

  setLocalStorage(INTRO_SETTINGS, { ...currentValue, ...dataWithValue });
};

export const getIntroSettings = (key = null) => {
  const introSettings = getFromLocalStorage(INTRO_SETTINGS);

  const hasExpired =
    !introSettings ||
    introSettings.last_updated + INTRO_SETTINGS_LIFE < Date.now();

  if (hasExpired) {
    deleteIntroSettings();
    return null;
  }

  return key ? introSettings[key] : introSettings;
};

/* introSession is stored in localStorage and expires in 1 week */
const INTRO_SESSION = "introSession";
const INTRO_SESSION_LIFE = 1000 * 3600 * 24 * 7;
const sessionKeys = [
  "last_updated",
  "selectedProducts",
  "cart",
  "cartId",
  "customerId",
  "userEmail",
  "subscriptionStatus",
  "subscriptionsData",
  "resultsShown",
];

export const setIntroSession = (state) => {
  const currentValue = getIntroSession();
  const newSession = state.session;

  const dataWithValue = Object.fromEntries(
    Object.entries(newSession).filter(
      ([key, val]) =>
        sessionKeys.includes(key) && val != null && typeof val !== "undefined",
    ),
  );

  dataWithValue.last_updated = Date.now();

  setLocalStorage(INTRO_SESSION, {
    ...currentValue,
    ...dataWithValue,
  });
};

export const getIntroSession = (key = null) => {
  const introSession = getFromLocalStorage(INTRO_SESSION);
  const hasExpired =
    !introSession ||
    introSession.last_updated + INTRO_SESSION_LIFE < Date.now();

  if (hasExpired) {
    deleteIntroSession();
    return null;
  }
  return key ? introSession[key] : introSession;
};

export const setIntroPersistance = (state) => {
  setIntroSession(state);
  setIntroSettings(state);
};

export const deleteIntroSession = () => {
  removeLocalStorageKey(INTRO_SESSION);
};

export const deleteIntroSettings = () => {
  removeLocalStorageKey(INTRO_SETTINGS);
};

export const deleteIntroSessionProducts = () => {
  const introSession = getIntroSession();
  if (introSession) {
    const { selectedProducts, cart, cartId, ...rest } = introSession;
    rest.last_updated = Date.now();
    setLocalStorage(INTRO_SESSION, rest);
  }
};

export const setCartId = (cartId) => {
  const introSession = getIntroSession() || {};
  setIntroSession({
    ...introSession,
    fl_cart_id: cartId,
  });
};

export const getUserEmail = () => {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("userEmail");
  } catch (ex) {
    console.error("Error getting user email:", ex);
  }
};

export const deleteCartId = () => {
  const introSession = getIntroSession();
  if (introSession) {
    const { fl_cart_id, ...rest } = introSession;
    rest.last_updated = Date.now();
    setLocalStorage(INTRO_SESSION, rest);
  }
};
